// Safe return-URL sanitization used after sign-in / sign-up.
//
// Goals:
//  - Never redirect to an off-site URL (open-redirect protection).
//  - Never pin a paid user back to /payment, /payment-failure, or /auth.
//  - Only allow paths the app actually owns (allowlist) so that crafted
//    `?next=` values can't trick us into navigating to unknown routes.
//
// If the input fails any check, we fall back to the learner's home, which is
// itself gated by PaidRoute — so unpaid users still get sent to /payment
// from there, but they will never be *stuck* on /payment after sign-in.
//
// Sprint 1 UX integration: the Future Command Center™ is the learner's daily
// home, so it — not the classic Dashboard — is where a returning learner
// lands. `/command-center` sends learners who have no completed report on to
// the assessment flow, so both cases are handled by one destination.

export const DEFAULT_POST_AUTH_ROUTE = "/home";

// Routes a freshly-authenticated user is allowed to land on. Keep this
// in sync with src/App.tsx — add new app routes here when they should
// be reachable as a post-auth deep link.
const ALLOWED_RETURN_ROUTES: ReadonlySet<string> = new Set([
  "/home",
  "/command-center",
  "/dashboard",
  "/assessment",
  "/mirror",
  "/report",
  "/portfolio",
  "/vision-board",
  "/coach",
  "/settings",
  "/profile",
  "/refer",
  "/affiliate",
  "/leaderboard",
  "/journey",
  "/redeem",
  "/careers",
  "/parent-report",
  "/reflect",
  "/parent",

  "/wrapped",
  "/journal",
  "/goals",
  "/challenges",
  "/opportunities",
  "/interview",
  "/resume",
  "/growth-plan",
  "/card",
  "/faqs",
  "/wrapped",
  "/.lovable/oauth/consent",
]);

// Paths an authenticated user should *never* be sent back to.
const FORBIDDEN_RETURN_ROUTES: ReadonlySet<string> = new Set([
  "/auth",
  "/payment",
  "/payment-failure",
  "/payment-success",
]);

export function sanitizeReturnTo(
  raw: string | null | undefined,
  fallback: string = DEFAULT_POST_AUTH_ROUTE,
): string {
  if (!raw || typeof raw !== "string") return fallback;
  const trimmed = raw.trim();
  if (!trimmed) return fallback;

  // Must be a same-origin relative path.
  if (!trimmed.startsWith("/")) return fallback;
  // Block protocol-relative ("//evil.com") and back-slash tricks ("/\evil.com").
  if (trimmed.startsWith("//") || trimmed.startsWith("/\\")) return fallback;
  // Block embedded schemes & whitespace.
  if (/[\s\u0000-\u001f]/.test(trimmed)) return fallback;
  if (/^\/?(javascript|data|vbscript):/i.test(trimmed)) return fallback;

  // Extract the base path (strip ?query and #hash) for allowlist matching.
  const base = trimmed.split(/[?#]/, 1)[0];
  if (FORBIDDEN_RETURN_ROUTES.has(base)) return fallback;
  // Allow exact matches and deep links beneath an allowed parent route
  // (e.g. "/careers/abc-123", "/assessment/v2", "/reflect/3", "/parent/xyz").
  if (!ALLOWED_RETURN_ROUTES.has(base)) {
    const parentAllowed = [...ALLOWED_RETURN_ROUTES].some(
      (route) => route !== "/" && base.startsWith(route + "/"),
    );
    if (!parentAllowed) return fallback;
    // A deep link must not slip past the forbidden list either.
    if ([...FORBIDDEN_RETURN_ROUTES].some((r) => base.startsWith(r + "/"))) {
      return fallback;
    }
  }


  return trimmed;
}

/**
 * Resolve the post-auth destination from both `?next=` and router
 * `location.state.from`, preferring `next` when present.
 */
export function resolvePostAuthDestination(opts: {
  next?: string | null;
  from?: string | null;
}): string {
  return sanitizeReturnTo(opts.next ?? opts.from ?? null);
}
